import { Cross, Languages, Waves, HandHeart } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { MaskLines, Overline, FadeUp } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/context/LangContext";
import { useImg } from "@/lib/design";
import { PageLayout } from "@/components/PageLayout";

const VALUES = [
  ["v1", Cross, "md:col-span-2 md:row-span-2"],
  ["v2", Languages, ""],
  ["v3", Waves, ""],
  ["v4", HandHeart, "md:col-span-2"],
];

export default function About() {
  const { t } = useLang();
  const atoll = useImg("about_atoll");
  return (
    <main data-testid="about-page">
      <PageLayout page="about" sections={{
        hero: <PageHero testid="about-hero" over={t("about.over")} title={t("about.title")} lead={t("about.lead")} imgKey="about_hero" />,
        mission: <Mission t={t} />,
        values: <Values t={t} atoll={atoll} />,
        faith: <Faith t={t} />,
      }} />
    </main>
  );
}

const Mission = ({ t }) => (
      <section className="mx-auto grid max-w-[1400px] gap-10 px-5 py-20 md:grid-cols-12 md:px-10">
        <div className="md:col-span-4">
          <Overline>{t("about.missionTitle")}</Overline>
        </div>
        <FadeUp className="md:col-span-8">
          <p data-testid="about-mission-text" className="font-display text-3xl leading-[1.25] text-[#F4EEE2] md:text-5xl">{t("about.mission")}</p>
        </FadeUp>
      </section>
);

const Values = ({ t, atoll }) => (
      <section data-testid="about-values-bento" className="mx-auto grid max-w-[1400px] auto-rows-[minmax(220px,auto)] gap-4 px-5 py-10 md:grid-cols-4 md:px-10">
        {VALUES.map(([k, Icon, span], i) => (
          <FadeUp key={k} delay={i * 0.08} className={`group relative flex flex-col justify-between overflow-hidden rounded-[28px] border border-[#F4EEE2]/10 bg-[#131C2E] p-7 transition-colors duration-500 hover:border-[#D4AF37]/40 ${span}`}>
            {i === 0 && <img src={atoll} data-ve-img="about_atoll" alt="Atoll from above" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-[1.4s] group-hover:scale-105" />}
            {i === 0 && <div className="absolute inset-0 bg-gradient-to-t from-[#0B111E] to-transparent" />}
            <Icon className="relative h-7 w-7 text-[#D4AF37]" strokeWidth={1.5} />
            <div className="relative mt-10">
              <h3 className={`font-display font-semibold text-[#F4EEE2] ${i === 0 ? "text-4xl md:text-5xl" : "text-3xl"}`}>{t(`about.${k}t`)}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-[#C9D0DB]">{t(`about.${k}`)}</p>
            </div>
          </FadeUp>
        ))}
      </section>
);

const Faith = ({ t }) => (
      <section className="mx-auto grid max-w-[1400px] gap-10 px-5 py-24 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <MaskLines lines={[t("about.faithTitle")]} className="font-display text-5xl font-semibold text-[#F4EEE2] md:text-6xl" />
          <p className="mt-4 text-sm text-[#B7BFCC]">{t("about.faithNote")}</p>
        </div>
        <FadeUp className="md:col-span-7">
          <Accordion type="single" collapsible defaultValue="f1" data-testid="faith-accordion">
            {["f1", "f2", "f3", "f4"].map((f, i) => (
              <AccordionItem key={f} value={f} className="border-[#F4EEE2]/10">
                <AccordionTrigger data-testid={`faith-item-${f}`} className="py-6 text-left font-display text-2xl text-[#F4EEE2] hover:no-underline md:text-3xl">
                  <span><span className="mr-5 font-mono text-xs text-[#D4AF37]">0{i + 1}</span>{t(`about.${f}q`)}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pl-10 text-base leading-relaxed text-[#C9D0DB]">{t(`about.${f}a`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </section>
);
